import {
  AutocompleteItem,
  AutocompleteSection,
  Autocomplete as NAutocomplete,
  type AutocompleteProps as Props,
} from '@heroui/react';
import type { Key } from 'react';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import { useInitialized } from '@/core/hooks/useInitialized';
import { isEmpty } from '@/utils';
import { cn } from '../../cn';

interface ISelectItem {
  label: string;
  key: string | number;
  description?: string | null;
}

interface ISelectItemSection {
  label: string;
  items: ISelectItem[];
}

export type AutoCompleteProps = Omit<Props, 'children'> & {
  options: ISelectItem[];
  sections?: ISelectItemSection[];
  children?: Props['children'];
  renderItem?: (item: ISelectItem) => React.ReactNode;
};

export const AutoComplete = ({
  options,
  sections,
  isVirtualized,
  children,
  className,
  renderItem,
  ...props
}: AutoCompleteProps) => {
  const nested = sections
    ? sections.map((s) => (
        <AutocompleteSection key={s.label} showDivider title={s.label}>
          {s.items.map((o) => (
            <AutocompleteItem textValue={o.label} className='text-default-700' key={o.key}>
              {renderItem ? (
                renderItem(o)
              ) : (
                <div className='flex flex-col space-y-1'>
                  <span className='text-small capitalize'>{o.label}</span>
                  {o.description && <span className='text-tiny text-default-400'>{o.description}</span>}
                </div>
              )}
            </AutocompleteItem>
          ))}
        </AutocompleteSection>
      ))
    : options.map((o) => (
        <AutocompleteItem textValue={o.label} className='text-default-700' key={o.key}>
          {renderItem ? (
            renderItem(o)
          ) : (
            <div className='flex flex-col space-y-1'>
              <span className='text-small capitalize'>{o.label}</span>
              {o.description && <span className='text-tiny text-default-400'>{o.description}</span>}
            </div>
          )}
        </AutocompleteItem>
      ));
  return (
    <NAutocomplete
      isVirtualized={isVirtualized}
      className={cn('placeholder:text-default-400', className)}
      popoverProps={{
        classNames: { content: 'rounded-md' },
      }}
      classNames={{}}
      radius='sm'
      {...props}
    >
      {isVirtualized
        ? options.map((o) => (
            <AutocompleteItem textValue={o.label} className='text-default-700 capitalize' key={o.key}>
              <div className='flex flex-col space-y-1'>
                <span className='text-small'>{o.label}</span>
                {o.description && <span className='text-tiny text-default-400'>{o.description}</span>}
              </div>
            </AutocompleteItem>
          ))
        : children || nested}
    </NAutocomplete>
  );
};

export interface FormAutoCompleteProps<T extends FieldValues>
  extends Omit<AutoCompleteProps, 'onValueChange'> {
  name: Path<T>;
  control: Control<T, unknown>;
  onValueChange?: (value: PathValue<T, Path<T>>) => void;
  transform?: (val: string) => PathValue<T, Path<T>>;
  extra?: ISelectItem[];
  defaultValue?: PathValue<T, Path<T>>;
  onInputChange?: (val: string) => void;
  shouldUnregister?: boolean;
}

export const FormAutoComplete = <T extends FieldValues>({
  control,
  onValueChange,
  transform,
  name,
  extra,
  defaultValue,
  onInputChange,
  shouldUnregister,
  allowsCustomValue = false,
  ...rest
}: FormAutoCompleteProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, onBlur, value }, fieldState: { error, invalid, isDirty } }) => {
        const onHandleChange = (val: Key | null) => {
          if (transform && !isEmpty(val)) {
            const newVal = transform(val as string);
            onChange(newVal);
            if (onValueChange) {
              onValueChange(newVal);
            }
            return;
          }
          if (onValueChange) {
            onValueChange(val as never);
          }
          onChange(val);
        };

        // biome-ignore lint/correctness/useHookAtTopLevel: <disabled>
        useInitialized((val) => {
          if (onInputChange && val) {
            onInputChange(typeof val === 'string' ? val : (val?.toString() ?? ''));
          }
        }, value);

        return (
          <AutoComplete
            {...rest}
            allowsCustomValue={allowsCustomValue}
            selectedKey={allowsCustomValue ? value : value != null ? `${value}` : ''}
            onBlur={onBlur}
            onSelectionChange={onHandleChange}
            isInvalid={isDirty && invalid}
            errorMessage={error?.message}
            onInputChange={onInputChange}
          />
        );
      }}
    />
  );
};
